import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { EmphasisBanner } from '@uhg-abyss/web/ui/EmphasisBanner';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../../common/ConstantsStyles';
import { makeAllSlidesAriaHidden } from '../../../../../common/Utils/a11yUtils/a11yUtils';
import { useLagoon } from '../../../../../hooks/useLagoon';
import {
  getAllProvidersWithServiceType,
  setVirtualCareListViewPageParam,
} from '../utils';
import { VirtualCareProviderCard } from './VirtualCareProviderCard';
import {
  carouselStyle,
  emphasisBannerStyle,
  slideContainerStyle,
  slideStyle,
  viewAllCardCss,
  viewAllLinkCss,
  virtualCareBannerContentStyle,
  virtualCareBannerHeaderStyle,
  virtualCareBannerLinkStyle,
} from './VirtualCareProvidersBanner.styled';
import { VirtualCareProviderModal } from './VirtualCareProvidersModal';

type VirtualCareProvidersBannerProps = {
  virtualCareProviders: any;
  setVirtualCareListView: (a: boolean) => void;
  expandedProviderListView: boolean;
};

export const VirtualCareProvidersBanner = ({
  expandedProviderListView = false,
  virtualCareProviders,
  setVirtualCareListView,
}: VirtualCareProvidersBannerProps) => {
  const [visibleAlerts, setVisibleAlerts] = useState([true, true]);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProviderName, setSelectedProviderName] = useState<
    string | null
  >(null);
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(mobileOnly);
  const data = useLagoon('ui-messaging')();
  const singleProviderContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_CONTENT_FOR_ONE,
  });
  const bannerContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_CONTENT,
  });
  const linkContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_LINK,
  });
  const allProviders = getAllProvidersWithServiceType(virtualCareProviders);

  const virtualCareProviderCount = allProviders?.length;
  const bannerMessage =
    (virtualCareProviderCount > 1
      ? bannerContent?.message
      : singleProviderContent?.message
    )?.replace('${count}', virtualCareProviderCount) || '';

  const slides = [
    ...allProviders?.slice(0, 3).map((provider) => (
      <Slide css={slideStyle} key="slide">
        <div css={slideContainerStyle} key="slide">
          <VirtualCareProviderCard
            key={provider?.providerName}
            mapView={!expandedProviderListView}
            mobileScreen={mobileScreen}
            provider={provider}
            serviceType={
              virtualCareProviders?.serviceType || provider?.serviceType
            }
            setIsModalOpen={setIsModalOpen}
            setSelectedProviderName={setSelectedProviderName}
          />
        </div>
      </Slide>
    )),
  ];

  if (virtualCareProviderCount > 3) {
    slides.push(
      <Card
        css={viewAllCardCss(mobileScreen || !expandedProviderListView)}
        key="all-virtual-care-link"
        onClick={(e) => {
          e.preventDefault();
          // Update the URL with the new parameter
          const url = new URL(window.location.href);
          setVirtualCareListViewPageParam(url);
          setVirtualCareListView(true);
        }}
      >
        <Link
          afterIcon="chevron_right"
          css={viewAllLinkCss}
          data-auto-testid={`view-all-virtual-care-providers-link-text`}
          data-testid={`view-all-virtual-care-providers-link-text`}
        >
          {t('VIRTUAL_CARE_PROVIDERS_BANNER.ALL_VIRTUAL_CARE')}
        </Link>
      </Card>
    );
  }

  const bannerContentNode = (
    <Flex css={virtualCareBannerContentStyle} direction="column">
      <Flex
        css={virtualCareBannerHeaderStyle(
          mobileScreen,
          expandedProviderListView
        )}
      >
        <Text
          css={{
            'abyss-text-root': {
              color: '#323333',
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '22px',
            },
          }}
          data-testid="virtual-care-provider-banner-title"
          fontSize="lg"
        >
          {bannerContent?.title}
        </Text>
        <Text
          css={{
            'abyss-text-root': {
              color: '#323333',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '18px',
            },
          }}
          data-testid="virtual-care-provider-banner-description"
        >
          {bannerMessage}
        </Text>
      </Flex>
      <Link
        afterIcon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        css={virtualCareBannerLinkStyle}
        data-auto-testid="virtual-care-provider-link-text"
        data-testid="virtual-care-provider-link-text"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }}
      >
        {linkContent?.title}
      </Link>
      {isOpen &&
        (mobileScreen ||
        (!expandedProviderListView && virtualCareProviderCount > 2) ? (
          <Carousel
            css={carouselStyle(true)}
            data-auto-testid="virtual-care-provider-carousel"
            data-testid="virtual-care-provider-carousel"
            label=""
            nextSlideOnClick={() => makeAllSlidesAriaHidden()}
            prevSlideOnClick={() => makeAllSlidesAriaHidden()}
            slideIndexOnClick={() => makeAllSlidesAriaHidden()}
            slides={slides}
            slidesPerView={1}
          />
        ) : (
          <Flex
            css={{
              width: '100%',
              gap: '16px',
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }}
            direction="row"
          >
            {slides?.map((slide, index) => (
              <Flex
                css={{
                  flex: '0 0 auto',
                  marginBottom: '8px',
                }}
                key={`slide-${index}`}
              >
                {slide}
              </Flex>
            ))}
          </Flex>
        ))}
    </Flex>
  );

  return (
    <React.Fragment>
      {visibleAlerts[0] && (
        <EmphasisBanner
          color="emphasis4"
          css={emphasisBannerStyle(mobileScreen)}
          customContent={bannerContentNode}
          data-testid="virtual-care-provider-banner"
          iconBrand={{
            icon: 'virtual_care',
            variant: 'twotone',
          }}
          onClose={() => {
            setVisibleAlerts([false, visibleAlerts[1]]);
          }}
        />
      )}
      <VirtualCareProviderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedProviderName={selectedProviderName}
        virtualCareProviders={virtualCareProviders}
      />
      {expandedProviderListView && <Divider color="$core.color.neutral.10" />}
    </React.Fragment>
  );
};
